/**
 * Policy for the AI-assisted half of "Arrange script" (F-60): WHICH cells are
 * offered an AI suggestion, and HOW MANY go into one request. Pure logic —
 * the actual request (core/api.ts `suggestArrangeSplit`) and the merge back
 * into the preview (ArrangeLayer.tsx, via segment.ts `rawFromOffsets`) live
 * elsewhere.
 *
 * A cell is AI-eligible when NEITHER deterministic path found anything to
 * split (`raw.length === 1` — no sentence-ending punctuation AND no clause mark
 * to cut on; see segment.ts `segmentClauses`) AND the text is long enough that
 * leaving it as one row is genuinely a problem. Short single-sentence cells
 * (the overwhelmingly common case) are correctly never offered this — there is
 * nothing wrong with them.
 */
import { LONG_SENTENCE_MIN_CHARS } from "./segment.js";

/**
 * Below this length a single un-splittable sentence is just a normal short cell
 * — not "too long", nothing to fix. It is the SAME "too long" threshold the
 * deterministic clause splitter uses (segment.ts `LONG_SENTENCE_MIN_CHARS`), so
 * the instant path and the AI fallback agree on which sentences qualify; kept
 * as a re-export purely so this module's callers/tests read one local name.
 */
export const AI_SPLIT_MIN_CHARS = LONG_SENTENCE_MIN_CHARS;

/** Is this cell a candidate for an AI split suggestion? */
export function isAiEligible(raw: readonly string[], text: string): boolean {
  return raw.length === 1 && text.length >= AI_SPLIT_MIN_CHARS;
}

/**
 * Request-size bounds, matching the server's defaults (config.ts
 * `DEFAULT_ARRANGE_MAX_CELLS`/`DEFAULT_ARRANGE_MAX_CHARS_TOTAL`) so a normal
 * selection never round-trips into a 400 the client could have avoided; a
 * deployment that configures smaller server limits still just gets a 400
 * ArrangeLayer surfaces as an error, since the two are independently
 * configured and this is a client-side courtesy bound, not the enforcement.
 */
export const MAX_AI_REQUEST_CELLS = 20;
export const MAX_AI_REQUEST_CHARS = 8_000;

export interface AiCandidate {
  rowId: string;
  text: string;
}

export interface AiBatchSelection {
  /** Candidates that fit within both bounds, in input order. */
  batch: AiCandidate[];
  /** Candidates left out because a bound was reached (re-run picks these up). */
  skipped: number;
}

/**
 * Greedily fill one request up to `MAX_AI_REQUEST_CELLS` cells and
 * `MAX_AI_REQUEST_CHARS` total characters, in the given order. Once EITHER
 * bound would be exceeded, every remaining candidate is skipped (not
 * reordered to backfill with a smaller one) — simple, predictable, and the
 * skipped count tells the user exactly how many to pick up on a re-run.
 */
export function selectAiBatch(candidates: readonly AiCandidate[]): AiBatchSelection {
  const batch: AiCandidate[] = [];
  let chars = 0;
  for (const candidate of candidates) {
    if (batch.length >= MAX_AI_REQUEST_CELLS) break;
    if (chars + candidate.text.length > MAX_AI_REQUEST_CHARS) break;
    batch.push(candidate);
    chars += candidate.text.length;
  }
  return { batch, skipped: candidates.length - batch.length };
}
